import React, { useEffect, useState, useCallback } from 'react';
import { Download, X, Plus, ChevronLeft, ChevronRight, CheckCircle2, HelpCircle } from 'lucide-react';

const DISMISS_KEY = 'playcity-pwa-prompt-dismissed-at';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true);

const isIOS = () =>
  typeof window !== 'undefined' &&
  /iPad|iPhone|iPod/.test(window.navigator.userAgent) &&
  !window.MSStream;

const wasRecentlyDismissed = () => {
  try {
    const t = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    return t && Date.now() - t < DISMISS_TTL_MS;
  } catch (_) {
    return false;
  }
};

// Icône Safari Partager (carré + flèche haut)
const ShareIcon = ({ className = 'w-8 h-8' }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} strokeWidth={2.2}>
    <path d="M12 3 L12 15 M7 8 L12 3 L17 8" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5 12 L5 20 Q5 21 6 21 L18 21 Q19 21 19 20 L19 12" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Illustration mini Safari avec ⋯ surligné en BAS À DROITE
const Step1Illustration = () => (
  <div className="relative w-full h-48 rounded-2xl bg-gradient-to-b from-[#1a1a2e] to-[#0B132B] border border-[#FFD700]/20 overflow-hidden">
    {/* Téléphone simulé */}
    <div className="absolute inset-x-8 top-4 bottom-12 rounded-xl bg-[#000]/40 border border-white/10" />
    {/* Barre Safari simulée en bas */}
    <div className="absolute bottom-2 left-3 right-3 h-9 rounded-full bg-[#1c1c1e] flex items-center justify-between px-2 border border-white/15">
      <div className="w-7 h-7 rounded-full bg-white/8 flex items-center justify-center text-white/60 text-xs">‹</div>
      <div className="text-white/55 text-[10px] truncate flex-1 text-center">playcity.ci</div>
      {/* Bouton ⋯ surligné */}
      <div className="relative">
        <div className="w-7 h-7 rounded-full bg-[#FFD700] text-[#0B132B] font-black flex items-center justify-center text-sm shadow-[0_0_18px_rgba(255,215,0,0.9)] animate-pulse">⋯</div>
      </div>
    </div>
    {/* Flèche pointant vers ⋯ */}
    <svg
      className="absolute right-3 bottom-12 animate-bounce drop-shadow-[0_0_12px_rgba(255,215,0,0.85)]"
      width="34"
      height="50"
      viewBox="0 0 34 50"
      fill="none"
    >
      <path d="M17 4 L17 38 M6 30 L17 41 L28 30" stroke="#FFD700" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </div>
);

// Illustration menu Safari avec "Partager" surligné
const Step2Illustration = () => (
  <div className="relative w-full h-48 rounded-2xl bg-gradient-to-b from-[#1a1a2e] to-[#0B132B] border border-[#FFD700]/20 overflow-hidden p-3">
    <div className="absolute inset-x-3 inset-y-3 rounded-xl bg-[#1c1c1e] border border-white/10 p-2 flex flex-col gap-1.5">
      {/* Item Partager surligné */}
      <div className="relative flex items-center gap-2 px-2 py-1.5 rounded-md bg-[#FFD700]/15 border border-[#FFD700] animate-pulse">
        <div className="text-[#0B84FF]"><ShareIcon className="w-4 h-4" /></div>
        <div className="text-[#FFD700] text-xs font-bold">Partager</div>
        {/* Pointeur (main) */}
        <div className="absolute -right-2 top-1/2 -translate-y-1/2 text-2xl">👉</div>
      </div>
      <div className="flex items-center gap-2 px-2 py-1.5 opacity-50">
        <div className="w-4 h-4 rounded bg-white/20" />
        <div className="text-white/70 text-xs">Ajouter à Signets</div>
      </div>
      <div className="flex items-center gap-2 px-2 py-1.5 opacity-50">
        <div className="w-4 h-4 rounded bg-white/20" />
        <div className="text-white/70 text-xs">Nouvel onglet</div>
      </div>
      <div className="flex items-center gap-2 px-2 py-1.5 opacity-50">
        <div className="w-4 h-4 rounded bg-white/20" />
        <div className="text-white/70 text-xs">Onglet privé</div>
      </div>
    </div>
  </div>
);

// Illustration iOS share sheet — réaliste avec scroll indicator
const Step3Illustration = () => (
  <div className="relative w-full h-56 rounded-2xl bg-gradient-to-b from-[#1a1a2e] to-[#0B132B] border border-[#FFD700]/20 overflow-hidden p-2">
    {/* Mini share sheet iOS */}
    <div className="absolute inset-x-2 top-2 bottom-2 rounded-2xl bg-[#1c1c1e] border border-white/10 p-2 flex flex-col gap-1.5">
      {/* Header app */}
      <div className="flex items-center gap-2 pb-1 border-b border-white/10">
        <div className="w-6 h-6 rounded-md bg-gradient-to-br from-[#FFD700] to-[#C6A15B] flex items-center justify-center text-[#0B132B] font-black text-[8px]">PC</div>
        <div className="flex-1 min-w-0">
          <div className="text-white text-[9px] font-bold truncate">PlayCity</div>
          <div className="text-white/40 text-[7px] truncate">playcity.ci</div>
        </div>
      </div>
      {/* Apps row */}
      <div className="flex items-center gap-1.5 overflow-hidden">
        <div className="shrink-0 flex flex-col items-center gap-0.5"><div className="w-7 h-7 rounded-lg bg-blue-400" /><div className="text-white/60 text-[7px]">AirDrop</div></div>
        <div className="shrink-0 flex flex-col items-center gap-0.5"><div className="w-7 h-7 rounded-lg bg-green-500" /><div className="text-white/60 text-[7px]">SMS</div></div>
        <div className="shrink-0 flex flex-col items-center gap-0.5"><div className="w-7 h-7 rounded-lg bg-blue-500" /><div className="text-white/60 text-[7px]">Mail</div></div>
        <div className="shrink-0 flex flex-col items-center gap-0.5"><div className="w-7 h-7 rounded-lg bg-yellow-300" /><div className="text-white/60 text-[7px]">Notes</div></div>
      </div>
      {/* Actions row scrollable - SCROLL INDICATOR */}
      <div className="relative mt-1 flex-1 flex flex-col">
        <div className="text-[#FFD700] text-[9px] text-center font-bold flex items-center justify-center gap-1 mb-1 animate-pulse">
          <span>← Faites défiler ce menu vers le bas →</span>
        </div>
        <div className="flex-1 rounded-xl bg-[#2c2c2e]/50 p-1.5 flex flex-col gap-1 overflow-hidden">
          <div className="flex items-center gap-1.5 px-1 opacity-50">
            <div className="w-3 h-3 rounded-sm bg-white/30" />
            <div className="text-white/60 text-[8px]">Copier</div>
          </div>
          <div className="flex items-center gap-1.5 px-1 opacity-50">
            <div className="w-3 h-3 rounded-sm bg-white/30" />
            <div className="text-white/60 text-[8px]">Ajouter à Signets</div>
          </div>
          {/* Sur l'écran d'accueil — surligné */}
          <div className="relative flex items-center gap-1.5 px-1.5 py-1 rounded-md bg-[#FFD700]/15 border border-[#FFD700] animate-pulse">
            <div className="w-4 h-4 rounded bg-[#FFD700] text-[#0B132B] flex items-center justify-center font-black text-[10px]">+</div>
            <div className="text-[#FFD700] text-[9px] font-bold">Sur l’écran d’accueil</div>
            <div className="absolute -right-1 top-1/2 -translate-y-1/2 text-base">👉</div>
          </div>
          <div className="flex items-center gap-1.5 px-1 opacity-50">
            <div className="w-3 h-3 rounded-sm bg-white/30" />
            <div className="text-white/60 text-[8px]">Marquer comme favori</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Illustration finale "Ajouter" en haut à droite
const Step4Illustration = () => (
  <div className="relative w-full h-44 rounded-2xl bg-gradient-to-b from-[#1a1a2e] to-[#0B132B] border border-[#FFD700]/20 overflow-hidden p-2">
    <div className="absolute inset-x-2 inset-y-2 rounded-xl bg-[#1c1c1e] border border-white/10 p-2">
      {/* Header avec Annuler / Ajouter */}
      <div className="flex items-center justify-between border-b border-white/10 pb-1.5">
        <div className="text-white/55 text-[10px]">Annuler</div>
        <div className="text-white text-xs font-bold">Sur l’écran d’accueil</div>
        <div className="relative">
          <div className="px-2 py-1 rounded-md bg-[#FFD700]/15 border border-[#FFD700] text-[#FFD700] font-black text-[10px] animate-pulse">
            Ajouter
          </div>
          <div className="absolute -right-2 top-1/2 -translate-y-1/2 text-base">👉</div>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-2">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FFD700] to-[#C6A15B] flex items-center justify-center text-[#0B132B] font-black text-xs shadow-lg">PC</div>
        <div className="flex-1">
          <div className="text-white text-[11px] font-bold">PlayCity</div>
          <div className="text-white/40 text-[9px]">playcity.ci</div>
        </div>
      </div>
      <div className="text-white/50 text-[9px] text-center mt-2">L’icône apparaîtra sur votre écran d’accueil</div>
    </div>
  </div>
);

const STEPS = [
  {
    title: 'Touchez l’icône ⋯',
    subtitle: 'En bas à droite de Safari',
    Illustration: Step1Illustration,
    cta: 'J’ai touché ⋯',
  },
  {
    title: 'Touchez « Partager »',
    subtitle: 'Le 1er élément du menu (icône bleue ↑)',
    Illustration: Step2Illustration,
    cta: 'J’ai touché Partager',
  },
  {
    title: 'Faites défiler puis touchez « Sur l’écran d’accueil »',
    subtitle: 'Le menu se déroule vers le bas — cherchez « Sur l’écran d’accueil » avec l’icône ➕',
    Illustration: Step3Illustration,
    cta: 'J’ai touché Sur l’écran d’accueil',
  },
  {
    title: 'Touchez « Ajouter »',
    subtitle: 'En haut à droite — l’icône PlayCity arrivera sur votre écran d’accueil',
    Illustration: Step4Illustration,
    cta: 'Terminer',
  },
];

const PWAInstallPrompt = () => {
  const [deferredEvt, setDeferredEvt] = useState(null);
  const [bannerVisible, setBannerVisible] = useState(false);
  const [iosWizardOpen, setIosWizardOpen] = useState(false);
  const [step, setStep] = useState(0); // 0..2
  const [done, setDone] = useState(false);
  const [installed, setInstalled] = useState(false);

  const ios = isIOS();
  const standalone = isStandalone();

  useEffect(() => {
    if (standalone) {
      setInstalled(true);
      return;
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredEvt(e);
      if (!wasRecentlyDismissed()) setBannerVisible(true);
    };
    const onInstalled = () => {
      setBannerVisible(false);
      setIosWizardOpen(false);
      setDeferredEvt(null);
      setInstalled(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    let t;
    if (ios && !wasRecentlyDismissed()) {
      t = setTimeout(() => setBannerVisible(true), 1500);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      if (t) clearTimeout(t);
    };
  }, [ios, standalone]);

  const dismissBanner = useCallback(() => {
    setBannerVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (_) {}
  }, []);

  const handleInstallClick = useCallback(async () => {
    if (ios) {
      setBannerVisible(false);
      setStep(0);
      setDone(false);
      setIosWizardOpen(true);
      return;
    }
    if (!deferredEvt) return;
    deferredEvt.prompt();
    const choice = await deferredEvt.userChoice;
    if (choice.outcome === 'accepted') setBannerVisible(false);
    setDeferredEvt(null);
  }, [deferredEvt, ios]);

  const closeWizard = () => setIosWizardOpen(false);
  const next = () => {
    if (step < STEPS.length - 1) setStep(step + 1);
    else setDone(true);
  };
  const prev = () => {
    if (done) {
      setDone(false);
      return;
    }
    if (step > 0) setStep(step - 1);
  };

  if (installed) return null;

  const current = STEPS[step];

  // FAB d'aide persistant : visible en permanence sur iOS si l'app n'est pas installée
  const showHelpFab = ios && !standalone && !iosWizardOpen && !bannerVisible;

  return (
    <>
      {/* FAB persistant "?" pour réouvrir le guide à tout moment */}
      {showHelpFab && (
        <button
          type="button"
          onClick={() => {
            setStep(0);
            setDone(false);
            setIosWizardOpen(true);
          }}
          data-testid="pwa-help-fab"
          aria-label="Aide installation"
          className="fixed z-[90] left-3 bottom-[88px] xl:left-auto xl:right-6 xl:bottom-6 w-12 h-12 rounded-full bg-gradient-to-br from-[#FFD700] to-[#C6A15B] hover:from-[#FFE040] hover:to-[#D4B06B] text-[#0B132B] shadow-2xl shadow-[#FFD700]/40 border-2 border-[#FFD700] flex items-center justify-center transition-transform hover:scale-110 active:scale-95"
          style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <HelpCircle className="w-6 h-6" strokeWidth={2.5} />
        </button>
      )}
      {/* Bandeau "Installer PlayCity" */}
      {bannerVisible && (
        <div
          data-testid="pwa-install-prompt"
          className="fixed left-3 right-3 bottom-[88px] xl:bottom-6 xl:left-auto xl:right-6 xl:max-w-sm z-[100] rounded-2xl bg-gradient-to-br from-[#0B132B] to-[#1C2541] border-2 border-[#C6A15B] shadow-2xl shadow-[#C6A15B]/25 p-4"
          style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
        >
          <button
            type="button"
            onClick={dismissBanner}
            aria-label="Fermer"
            data-testid="pwa-prompt-close-btn"
            className="absolute top-2 right-2 p-1.5 rounded-md hover:bg-[#C6A15B]/15 text-[#EAEAEA]/70"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-3 pr-6">
            <div className="shrink-0 w-11 h-11 rounded-xl bg-[#FFD700]/20 border border-[#FFD700]/40 flex items-center justify-center">
              <Download className="w-5 h-5 text-[#FFD700]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[#FFD700] font-bold text-base leading-tight">
                Installer PlayCity
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={handleInstallClick}
            data-testid="pwa-install-btn"
            className="mt-3 w-full bg-gradient-to-r from-[#C6A15B] to-[#B89149] hover:from-[#D4B06B] hover:to-[#C6A15B] text-[#0B132B] font-bold text-sm py-2.5 px-3 rounded-xl transition-all shadow-md"
          >
            Installer
          </button>
        </div>
      )}

      {/* Wizard interactif iOS — 3 étapes pas-à-pas */}
      {iosWizardOpen && ios && (
        <div
          data-testid="pwa-ios-wizard"
          className="fixed inset-0 z-[120] bg-black/90 backdrop-blur-md overflow-y-auto"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
        >
          <div className="min-h-full flex items-start justify-center p-3">
            <div className="w-full max-w-md rounded-3xl bg-gradient-to-br from-[#0B132B] to-[#1C2541] border-2 border-[#FFD700] shadow-[0_20px_60px_rgba(255,215,0,0.4)] overflow-hidden mt-6 mb-6">
              {/* Header */}
              <div className="px-5 pt-4 pb-3 border-b border-[#FFD700]/15 relative">
                <button
                  type="button"
                  onClick={closeWizard}
                  aria-label="Fermer"
                  data-testid="pwa-wizard-close"
                  className="absolute top-3 right-3 p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="text-[#FFD700] font-black text-xl text-center pr-6">
                  Installer PlayCity
                </div>
                {/* Progress dots */}
                <div className="flex items-center justify-center gap-2 mt-3">
                  {STEPS.map((_, i) => (
                    <div
                      key={i}
                      className={`h-1.5 rounded-full transition-all ${
                        done || i < step
                          ? 'w-8 bg-[#FFD700]'
                          : i === step
                          ? 'w-8 bg-[#FFD700]'
                          : 'w-3 bg-white/20'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Body */}
              {!done ? (
                <div className="p-5 space-y-4">
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#FFD700] text-[#0B132B] font-black text-2xl flex items-center justify-center shrink-0">
                      {step + 1}
                    </div>
                    <div className="flex-1">
                      <div className="text-white font-black text-lg leading-tight">
                        {current.title}
                      </div>
                      <div className="text-[#EAEAEA]/65 text-xs mt-1">
                        {current.subtitle}
                      </div>
                    </div>
                  </div>

                  {/* Illustration */}
                  <current.Illustration />

                  <div className="text-[#EAEAEA]/55 text-xs text-center">
                    Étape {step + 1} sur {STEPS.length}
                  </div>
                </div>
              ) : (
                <div className="p-6 text-center space-y-3">
                  <div className="w-16 h-16 rounded-full bg-[#FFD700]/20 border-2 border-[#FFD700] flex items-center justify-center mx-auto">
                    <CheckCircle2 className="w-9 h-9 text-[#FFD700]" />
                  </div>
                  <div className="text-[#FFD700] font-black text-xl">Bravo !</div>
                  <div className="text-[#EAEAEA]/80 text-sm leading-relaxed">
                    Suivez les 3 étapes ci-dessus dans Safari.<br />
                    L’icône PlayCity apparaîtra sur votre écran d’accueil.
                  </div>
                  <div className="text-[#EAEAEA]/50 text-xs mt-2">
                    À partir de là, ouvrez PlayCity directement depuis l’icône comme une vraie app.
                  </div>
                </div>
              )}

              {/* Navigation buttons */}
              <div className="px-5 pb-5 pt-1 flex gap-2">
                {(step > 0 || done) && (
                  <button
                    type="button"
                    onClick={prev}
                    data-testid="pwa-wizard-prev"
                    className="flex-1 bg-[#1C2541] hover:bg-[#252e54] border border-[#C6A15B]/30 text-[#EAEAEA] font-bold text-sm py-3 rounded-xl transition-all flex items-center justify-center gap-1"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Précédent
                  </button>
                )}
                {!done ? (
                  <button
                    type="button"
                    onClick={next}
                    data-testid="pwa-wizard-next"
                    className="flex-1 bg-gradient-to-r from-[#C6A15B] to-[#B89149] hover:from-[#D4B06B] hover:to-[#C6A15B] text-[#0B132B] font-bold text-sm py-3 rounded-xl transition-all shadow-md flex items-center justify-center gap-1"
                  >
                    {current.cta}
                    {step < STEPS.length - 1 && <ChevronRight className="w-4 h-4" />}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={closeWizard}
                    data-testid="pwa-wizard-finish"
                    className="flex-1 bg-gradient-to-r from-[#C6A15B] to-[#B89149] hover:from-[#D4B06B] hover:to-[#C6A15B] text-[#0B132B] font-bold text-sm py-3 rounded-xl transition-all shadow-md"
                  >
                    Fermer
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallPrompt;
